<template>
  <div v-if="data" class="grow border-l border-transparent"
    :class="filteredHits.length ? 'text-Success' : 'text-Error'">
    {{ $t('search.results', filteredHits.length) }}
  </div>

  <div v-if="filteredHits.length > 0" class="flex w-full flex-col">
    <TransitionGroup tag="div" name="results" class="results-grid style-scrollbar h-[80svh] max-h-max shrink grow pr-2">
      <div class="results-header text-h2 max-lg:hidden" key="rubrik">
        {{ $t('search.type') }}
      </div>
      <div class="results-header text-h2 max-lg:hidden" key="bez">
        {{ $t('search.designation') }}
      </div>
      <div class="results-header text-h2 max-lg:hidden" key="empthy" />
      <div v-if="filteredHits.length === 0" class="col-span-5 py-8 text-center">
        {{ $t('search.results', 0) }}
      </div>
      <template v-for="hit in filteredHits" :key="hit.objectID">
        <div class="pr-4 max-lg:hidden lg:pr-10">
          {{ $t('search.blueprint.' + hit.blueprint?.handle) }}
        </div>
        <div class="pr-4 lg:pr-10">
          <h3 class="mb-1 text-p2 !font-semibold md:mb-2 md:text-p1" v-html="formatTitle(hit)" />
          <template v-for="(highlightResult, index) in extractMatchLevelFull(
            hit._highlightResult,
          )" :key="index">
            <p class="highlight max-w-text line-clamp-2 text-sm not-last:mb-2 md:line-clamp-3"
              v-html="useFormat(highlightResult.value)?.trim?.()" />
          </template>
        </div>
        <ul
          class="max-lg:mt-2 max-lg:mb-10 max-lg:flex max-lg:flex-wrap max-lg:justify-end max-lg:gap-x-4 max-lg:gap-y-2 lg:pr-10">
          <li>
            <UiButton class="max-md:hidden" variant="secondary" icon="arrow-right" :to="localePath((hit as any).url)">{{
              $t('search.to_page') }}
            </UiButton>
            <NuxtLink class="text-p2 underline hover:underline-offset-2 md:hidden" :to="localePath((hit as any).url)">{{
              $t('search.to_page') }}
            </NuxtLink>
          </li>
        </ul>
      </template>
      <span class="block h-28 max-lg:hidden lg:col-span-3"></span>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
const localePath = useLocalePath()

const props = defineProps<{
  searchQuery: string
}>()
const searchQuery = computed(() => props.searchQuery)

const { search } = useAlgoliaSearch('default') // pass your index name as param
const { data, refresh } = await useAsyncData('ssr-search-results', async () => {
  if (searchQuery.value) {
    return await search({ query: searchQuery.value })
  }
  return null
})
const debouncedRefresh = useDebounceFn(
  async () => {
    await refresh()
  },
  700,
  { maxWait: 1100 },
)
watch(searchQuery, debouncedRefresh)

const { locale } = useI18n()
const filteredHits = computed<any[]>(() => {
  if (!data?.value) return []

  let filteredHits = data.value.hits.filter((hit: any) => {
    if (!hit.locale) return true
    return hit.locale === locale.value
  })

  const hitInSpecialTitle = filteredHits.findIndex((hit: any) => {
    return searchQuery.value.length > 3
      && ['medication', 'active_ingredient'].includes(hit.blueprint?.handle ?? '')
      && hit.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  }) !== -1
  if (hitInSpecialTitle) {
    // filter out all other hits in text
    filteredHits = filteredHits.filter((hit: any) => {
      return hit.title.toLowerCase().includes(searchQuery.value.toLowerCase())
    })
  }

  const desitinProductFound = filteredHits.findIndex((hit: any) => {
    return hit.title.toLowerCase().includes('desitin')
  }) !== -1

  if (desitinProductFound) {
    filteredHits.sort((a: any, b: any) => {
      const aIsDesitin = a.title.toLowerCase().indexOf('desitin') !== -1
      const bIsDesitin = b.title.toLowerCase().indexOf('desitin') !== -1
      return (bIsDesitin ? 1 : 0) - (aIsDesitin ? 1 : 0)
    })
  }

  return filteredHits
})

const dateFormat = new Intl.DateTimeFormat(locale.value, {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
})
const formatDate = (date: Date | string | null) => {
  if (!date) return null
  if (typeof date === 'string') {
    date = new Date(date)
  }
  return dateFormat.format(date)
}

const formatTitle = (hit: any) => {
  if (hit?.blueprint === 'assets') {
    // return useAssetDescription(hit, locale.value)
  } else {
    return useFormat(hit?.title ?? '')
  }
}

function extractMatchLevelFull(object: any, result: any[] = []) {
  for (const key in object) {
    if (object[key] && typeof object[key] === 'object') {
      if (
        (object[key] as any).hasOwnProperty('matchLevel') &&
        object[key].matchLevel !== 'none' &&
        !['url', 'title'].includes(key)
      ) {
        result.push(object[key])
      } else {
        extractMatchLevelFull(object[key], result)
      }
    }
  }
  return result
}
</script>

<style scoped>
@import 'tailwindcss/theme' theme(reference);
@import '../../assets/css/_colors.css' theme(reference);

.results-grid {
  @apply grid max-w-full gap-2 max-lg:grid-cols-1 lg:gap-x-0 lg:gap-y-10;
  position: relative;
  overflow-x: hidden;
  grid-template-columns: 2fr 5fr 1fr;
  grid-template-rows: auto;

  padding-top: 1.5rem;

  &>*:not(.results-header, ul) {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      border-top: 1px solid black;
      opacity: 0.2;
      z-index: 1;

      inline-size: 100%;
      inset-inline-start: 0rem;
      inset-block-start: -1rem;
    }
  }

  /* media query lg */
  @media screen and (min-width: theme('screens.lg')) {
    &>*:not(.results-header) {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        border-top: 1px solid black;
        opacity: 0.2;
        z-index: 1;

        inline-size: 100%;
        inset-inline-start: 0rem;
        inset-block-start: -1rem;
      }
    }
  }
}

.results-move,
/* apply transition to moving elements */
.results-enter-active,
.results-leave-active {
  transition: all 0.5s ease;
}

.results-enter-from,
.results-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.results-leave-active {
  position: absolute;
}

:deep(.highlight) em {
  @apply relative z-0 font-medium not-italic after:absolute after:-inset-x-px after:inset-y-0 after:-z-10 after:bg-Blue-Primary/45;
}
</style>
